import React from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, CheckCircle2 } from "lucide-react";
import { HID } from "@/constants/cosecIds";

export default function Hero() {
  return (
    <section
      className="relative overflow-hidden bg-primary text-primary-foreground isolate"
    >
      {/* Layered ambient glows for depth */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(60rem 40rem at 75% -10%, hsl(93 53% 51% / 0.18), transparent 60%), radial-gradient(50rem 35rem at 10% 60%, hsl(207 74% 50% / 0.22), transparent 55%)",
        }}
      />
      {/* Subtle dotgrid, masked away from the bottom fade so it doesn't show as a pattern on the light blend zone */}
      <div
        className="absolute inset-0 bg-dotgrid opacity-[0.08] pointer-events-none"
        style={{ maskImage: "linear-gradient(to bottom, black 0%, black 55%, transparent 90%)", WebkitMaskImage: "linear-gradient(to bottom, black 0%, black 55%, transparent 90%)" }}
      />
      {/* Diagonal sweep highlight */}
      <div
        className="absolute -top-32 -right-32 h-[40rem] w-[40rem] rounded-full pointer-events-none blur-[120px]"
        style={{ background: "radial-gradient(closest-side, hsl(93 53% 51% / 0.30), transparent 70%)" }}
      />
      {/* Soft accent rim line at top */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent/40 to-transparent pointer-events-none" />
      {/* Bottom horizon, pure gradient now sits on the section BG itself (no overlay → no banding). */}


      <div className="container-cv relative pt-14 md:pt-24 pb-16 md:pb-20 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-7 reveal">
          {/* Eyebrow pill */}
          <span className="inline-flex items-center gap-2 rounded-full bg-white/[0.06] backdrop-blur-md border border-white/15 text-[11px] md:text-xs font-bold uppercase tracking-[0.14em] text-white/90 px-3 py-1.5">
            <Sparkles className="h-3.5 w-3.5 text-accent" />
            AI-powered &middot; ISO 27001 &middot; UK Companies House authorised
          </span>

          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-[4.25rem] font-bold leading-[1.02] tracking-tight">
            <span className="block text-white">One Platform for</span>
            <span className="block text-white">Entity Management.</span>
            <span className="block text-white mt-3 text-3xl sm:text-4xl lg:text-5xl">Corporate Governance,{" "}
              <span className="relative inline-block">
                <span className="relative z-10 text-accent">Simplified.</span>
                <span className="absolute left-0 bottom-1 h-3 w-full bg-accent/25 -z-0 rounded-sm" />
              </span>
            </span>
          </h1>

          <p className="mt-6 max-w-2xl text-base md:text-lg text-white/70 leading-relaxed">
            CoSec is an AI-powered platform to manage corporate governance, compliance
            and company secretarial work in one place. Trusted by growing businesses
            and in-house legal teams to simplify entity management, reduce risk and stay compliant across jurisdictions.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <Button
              asChild
              data-testid={HID.heroBookDemo}
              className="h-12 px-6 bg-accent hover:bg-accent/90 text-primary font-bold text-base shadow-[0_10px_30px_-10px_hsl(93_53%_51%/0.7)] hover:shadow-[0_18px_40px_-12px_hsl(93_53%_51%/0.85)] transition-all"
            >
              <a href="/contact">
                Book a Demo <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
            <Button
              data-testid={HID.heroSeeProducts}
              variant="outline"
              onClick={() => document.getElementById("platform")?.scrollIntoView({ behavior: "smooth" })}
              className="h-12 px-6 border-white/25 text-white hover:bg-white hover:text-primary font-semibold text-base bg-white/[0.04] backdrop-blur-sm transition-all"
            >
              Explore the Platform
            </Button>
          </div>

          <div className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-3">
            {[
              "ISO 9001 Certified",
              "ISO 27001 Certified",
              "ISO 27701 Certified",
              "GDPR Compliant",
              "UK Companies House Authorised Software",
            ].map((t, i) => (
              <div key={i} className="flex items-center gap-2 text-xs md:text-sm font-semibold text-white/85">
                <CheckCircle2 className="h-4 w-4 text-accent" /> {t}
              </div>
            ))}
          </div>
        </div>

        {/* Right: real product hero banner */}
        <div className="lg:col-span-5 relative">
          <div className="relative mx-auto max-w-[520px]">
            {/* Soft halos behind the image */}
            <div
              className="absolute -inset-10 rounded-[2rem] pointer-events-none blur-2xl"
              style={{ background: "radial-gradient(closest-side, hsl(93 53% 51% / 0.28), transparent 75%)" }}
            />
            <div
              className="absolute -inset-12 rounded-[2.5rem] pointer-events-none blur-3xl opacity-60"
              style={{ background: "radial-gradient(closest-side, hsl(0 0% 100% / 0.08), transparent 70%)" }}
            />
            <img
              src="/assets/cosec-hero-banner.png"
              alt="CoSec dashboard. Key Dates Due by Month with Due and Overdue counters"
              className="relative w-full h-auto drop-shadow-[0_30px_80px_rgba(0,0,0,0.55)]"
              loading="eager"
              data-testid="hero-dashboard-image"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
